import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ReactComponent as AlgovexLockup } from '../assets/algovex-lockup-tagline-dark.svg';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset } from '../api';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', color: 'var(--red)', fontSize: 14 }}>
            Invalid reset link. Please request a new one.
          </div>
          <button onClick={() => navigate('/auth')} style={btnStyle(false)}>
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    if (password !== confirmPassword) {
      setFieldErrors({ confirm_password: ['Passwords do not match.'] });
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset({ token, new_password: password });
      setSuccess(true);
    } catch (err) {
      const data = err.response?.data;
      if (data?.detail) {
        setError(data.detail);
      } else if (data?.new_password) {
        setFieldErrors({ new_password: [].concat(data.new_password) });
      } else {
        setError(err.message || 'Something went wrong.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={containerStyle}>
      <Helmet>
        <title>Reset Password — Algovex</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
            <AlgovexLockup className="auth-lockup-logo" style={{ width: 260, height: 'auto' }} />
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
            Choose a new password
          </div>
        </div>

        {success ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: 'var(--teal)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
              Your password has been reset successfully.
            </div>
            <button onClick={() => navigate('/auth', { state: { mode: 'login' } })} style={btnStyle(false)}>
              Sign In
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <label style={labelStyle}>New Password</label>
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              style={inputStyle}
              autoFocus
            />
            {fieldErrors.new_password?.map((msg, i) => (
              <div key={i} style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>{msg}</div>
            ))}
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>
              At least 8 characters. Can't be entirely numeric or a commonly used password.
            </div>

            <label style={labelStyle}>Confirm Password</label>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              style={inputStyle}
            />
            {fieldErrors.confirm_password?.map((msg, i) => (
              <div key={i} style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>{msg}</div>
            ))}

            {error && (
              <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 8 }}>{error}</div>
            )}

            <button type="submit" disabled={loading} style={btnStyle(loading)}>
              {loading ? '…' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const containerStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  minHeight: '100%', background: 'var(--bg)',
};

const cardStyle = {
  width: 380, background: 'var(--card)', borderRadius: 12,
  padding: 32, boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
  border: '1px solid var(--border)',
};

const labelStyle = {
  display: 'block', fontSize: 13, fontWeight: 600,
  color: 'var(--text)', marginTop: 14, marginBottom: 4,
};

const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 14,
  border: '1px solid var(--border)', borderRadius: 6, boxSizing: 'border-box',
  background: 'var(--surface)', color: 'var(--text)',
};

const btnStyle = (loading) => ({
  marginTop: 16, width: '100%', padding: '10px 0',
  fontSize: 14, fontWeight: 700,
  background: loading ? 'var(--subtle)' : 'var(--teal)', color: 'var(--bg)',
  border: 'none', borderRadius: 8, cursor: loading ? 'not-allowed' : 'pointer',
});
